"use client";

import {
  Container,
  Title,
  Text,
  Stack,
  Box,
  Card,
  Group,
  Badge,
  SimpleGrid,
  Paper,
  Button,
  Flex,
  Divider,
  Image,
  Grid,
  ThemeIcon,
  List,
  Tabs,
} from "@mantine/core";
import {
  IconPalette,
  IconArrowRight,
  IconEye,
  IconSparkles,
  IconX,
} from "@tabler/icons-react";
import { Link } from "@/i18n/navigation";
import { useTranslations } from "next-intl";
import { colorSeasons } from "@/constants";
import OutfitGrid from "@/components/OutfitGrid";
import { useState, useEffect } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";

interface OutfitIdea {
  title: string;
  description: string;
  imageData: string;
}

export default function SoftAutumnVsDeepAutumnContent() {
  const t = useTranslations("softAutumnVsDeepAutumn");

  // Get the subseason data
  const softAutumnData = colorSeasons.AUTUMN.subseasons.SOFT;
  const deepAutumnData = colorSeasons.AUTUMN.subseasons.DEEP;

  // State for outfit data
  const [softAutumnOutfits, setSoftAutumnOutfits] = useState<OutfitIdea[]>([]);
  const [deepAutumnOutfits, setDeepAutumnOutfits] = useState<OutfitIdea[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch outfit data
  useEffect(() => {
    const fetchOutfitData = async () => {
      try {
        const [softAutumnDoc, deepAutumnDoc] = await Promise.all([
          getDoc(doc(db, "outfitIdeas", "AUTUMN_SOFT")),
          getDoc(doc(db, "outfitIdeas", "AUTUMN_DEEP")),
        ]);

        setSoftAutumnOutfits(softAutumnDoc.data()?.ideas || []);
        setDeepAutumnOutfits(deepAutumnDoc.data()?.ideas || []);
      } catch (error) {
        console.error("Error fetching outfit data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOutfitData();
  }, []);

  const comparisonPoints = [
    {
      aspect: "Color Intensity",
      softAutumn: "Muted, soft colors with gentle saturation",
      deepAutumn: "Rich, deep colors with strong saturation and intensity",
      icon: IconPalette,
    },
    {
      aspect: "Undertone",
      softAutumn: "Warm undertones with soft, muted characteristics",
      deepAutumn: "Warm undertones with deep, rich characteristics",
      icon: IconEye,
    },
    {
      aspect: "Best For",
      softAutumn: "Delicate, subtle coloring with gentle warmth",
      deepAutumn: "Deep, rich coloring with high contrast",
      icon: IconSparkles,
    },
    {
      aspect: "Avoid",
      softAutumn: "Bright, saturated colors",
      deepAutumn: "Light, pastel colors",
      icon: IconX,
    },
  ];

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        {/* Hero Section */}
        <Box ta="center">
          <Title order={1} size="h1" mb="md">
            Soft Autumn vs Deep Autumn
          </Title>
          <Text size="lg" c="dimmed" maw={600} mx="auto" mb="xl">
            Discover the key differences between these two warm autumn subseasons and find which one perfectly
            complements your natural coloring.
          </Text>

          {/* Season Moodboard */}
          <Box mb="xl">
            <Box
              mih={{ base: "300px", md: "400px" }}
              style={{
                position: "relative",
                borderRadius: "1rem",
                overflow: "hidden",
              }}
            >
              <Image
                src="/AUTUMN.webp"
                alt="Autumn season moodboard"
                width={800}
                height={400}
                style={{
                  borderRadius: "1rem",
                  objectFit: "cover",
                  objectPosition: "center",
                  width: "100%",
                  height: "100%",
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* Quick Comparison Overview */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md" ta="center">
            Quick Comparison Overview
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Paper withBorder p="lg" radius="md">
              <Stack gap="md" ta="center">
                <Text size="xl">{softAutumnData.emoji}</Text>
                <Title order={3}>Soft Autumn</Title>
                <Text size="sm" c="dimmed">
                  {softAutumnData.description.substring(0, 150)}...
                </Text>
                <Badge color="orange" variant="light">
                  Muted & Gentle
                </Badge>
              </Stack>
            </Paper>
            <Paper withBorder p="lg" radius="md">
              <Stack gap="md" ta="center">
                <Text size="xl">{deepAutumnData.emoji}</Text>
                <Title order={3}>Deep Autumn</Title>
                <Text size="sm" c="dimmed">
                  {deepAutumnData.description.substring(0, 150)}...
                </Text>
                <Badge color="orange" variant="light">
                  Rich & Deep
                </Badge>
              </Stack>
            </Paper>
          </SimpleGrid>
        </Card>

        {/* Detailed Comparison */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="lg" ta="center">
            Key Differences
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            {comparisonPoints.map((point, index) => (
              <Paper key={index} withBorder p="lg" radius="md">
                <Stack gap="md">
                  <Group>
                    <ThemeIcon color="orange" variant="light" size="lg">
                      <point.icon size={20} />
                    </ThemeIcon>
                    <Text fw={600} size="lg">
                      {point.aspect}
                    </Text>
                  </Group>
                  <Box>
                    <Text fw={500} c="orange" size="sm" mb="xs">
                      Soft Autumn:
                    </Text>
                    <Text size="sm" mb="md">
                      {point.softAutumn}
                    </Text>
                    <Text fw={500} c="orange" size="sm" mb="xs">
                      Deep Autumn:
                    </Text>
                    <Text size="sm">{point.deepAutumn}</Text>
                  </Box>
                </Stack>
              </Paper>
            ))}
          </SimpleGrid>
        </Card>

        {/* Color Palette Comparison */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="lg" ta="center">
            Color Palette Comparison
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
            {/* Soft Autumn Colors */}
            <Box>
              <Title order={3} mb="md" ta="center">
                Soft Autumn Colors
              </Title>
              <Stack gap="md">
                <Box>
                  <Text fw={500} mb="sm">
                    Primary Colors
                  </Text>
                  <SimpleGrid cols={4} spacing="xs">
                    {softAutumnData.primaryColors.slice(0, 8).map((color, index) => (
                      <Box
                        key={index}
                        style={{
                          backgroundColor: color.hex,
                          height: 40,
                          borderRadius: 4,
                          border: "1px solid #e0e0e0",
                        }}
                        title={`${color.name} - ${color.hex}`}
                      />
                    ))}
                  </SimpleGrid>
                </Box>
                <Box>
                  <Text fw={500} mb="sm">
                    Neutrals
                  </Text>
                  <SimpleGrid cols={4} spacing="xs">
                    {softAutumnData.neutrals.slice(0, 4).map((color, index) => (
                      <Box
                        key={index}
                        style={{
                          backgroundColor: color.hex,
                          height: 40,
                          borderRadius: 4,
                          border: "1px solid #e0e0e0",
                        }}
                        title={`${color.name} - ${color.hex}`}
                      />
                    ))}
                  </SimpleGrid>
                </Box>
              </Stack>
            </Box>

            {/* Deep Autumn Colors */}
            <Box>
              <Title order={3} mb="md" ta="center">
                Deep Autumn Colors
              </Title>
              <Stack gap="md">
                <Box>
                  <Text fw={500} mb="sm">
                    Primary Colors
                  </Text>
                  <SimpleGrid cols={4} spacing="xs">
                    {deepAutumnData.primaryColors.slice(0, 8).map((color, index) => (
                      <Box
                        key={index}
                        style={{
                          backgroundColor: color.hex,
                          height: 40,
                          borderRadius: 4,
                          border: "1px solid #e0e0e0",
                        }}
                        title={`${color.name} - ${color.hex}`}
                      />
                    ))}
                  </SimpleGrid>
                </Box>
                <Box>
                  <Text fw={500} mb="sm">
                    Neutrals
                  </Text>
                  <SimpleGrid cols={4} spacing="xs">
                    {deepAutumnData.neutrals.slice(0, 4).map((color, index) => (
                      <Box
                        key={index}
                        style={{
                          backgroundColor: color.hex,
                          height: 40,
                          borderRadius: 4,
                          border: "1px solid #e0e0e0",
                        }}
                        title={`${color.name} - ${color.hex}`}
                      />
                    ))}
                  </SimpleGrid>
                </Box>
              </Stack>
            </Box>
          </SimpleGrid>
        </Card>

        {/* Characteristics Comparison */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="lg" ta="center">
            Characteristics & Styling
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Box>
              <Title order={3} mb="md" ta="center" c="orange">
                Soft Autumn
              </Title>
              <Stack gap="md">
                <Box>
                  <Text fw={500} mb="sm">
                    Characteristics:
                  </Text>
                  <SimpleGrid cols={2} spacing="xs">
                    {softAutumnData.characteristics.map((char, index) => (
                      <Badge key={index} color="orange" variant="light" size="sm">
                        {char}
                      </Badge>
                    ))}
                  </SimpleGrid>
                </Box>
                <Box>
                  <Text fw={500} mb="sm">
                    Sample Clothing:
                  </Text>
                  <Text size="sm" c="dimmed">
                    {softAutumnData.sampleClothing}
                  </Text>
                </Box>
              </Stack>
            </Box>
            <Box>
              <Title order={3} mb="md" ta="center" c="orange">
                Deep Autumn
              </Title>
              <Stack gap="md">
                <Box>
                  <Text fw={500} mb="sm">
                    Characteristics:
                  </Text>
                  <SimpleGrid cols={2} spacing="xs">
                    {deepAutumnData.characteristics.map((char, index) => (
                      <Badge key={index} color="orange" variant="light" size="sm">
                        {char}
                      </Badge>
                    ))}
                  </SimpleGrid>
                </Box>
                <Box>
                  <Text fw={500} mb="sm">
                    Sample Clothing:
                  </Text>
                  <Text size="sm" c="dimmed">
                    {deepAutumnData.sampleClothing}
                  </Text>
                </Box>
              </Stack>
            </Box>
          </SimpleGrid>
        </Card>

        {/* How to Tell at Home */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="lg" ta="center">
            How to Tell the Difference at Home
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Box>
              <Title order={3} size="h4" mb="md">The Fabric Test</Title>
              <Text size="sm" c="dimmed" mb="sm">
                Hold a muted olive green and a rich chocolate brown near your face in natural light:
              </Text>
              <List size="sm" spacing="xs">
                <List.Item><strong>Soft Autumn:</strong> The muted olive makes your skin glow softly</List.Item>
                <List.Item><strong>Deep Autumn:</strong> The rich chocolate brown brings out your features</List.Item>
              </List>
            </Box>
            <Box>
              <Title order={3} size="h4" mb="md">The Contrast Test</Title>
              <Text size="sm" c="dimmed" mb="sm">
                Look at the contrast between your hair, skin, and eyes:
              </Text>
              <List size="sm" spacing="xs">
                <List.Item><strong>Soft Autumn:</strong> Low to medium contrast, features blend softly</List.Item>
                <List.Item><strong>Deep Autumn:</strong> Medium to high contrast, features are more distinct</List.Item>
              </List>
            </Box>
          </SimpleGrid>
          <Paper withBorder p="md" radius="md" mt="lg" bg="gray.0">
            <Text size="sm" c="dimmed">
              Still unsure? These tests can be tricky in artificial lighting or with makeup on. A photo analysis removes the guesswork.
            </Text>
          </Paper>
        </Card>

        {/* Final CTA */}
        <Card withBorder p="xl" radius="md" bg="orange.0">
          <Stack gap="lg" ta="center">
            <Title order={2} size="h2">
              Want a Definitive Answer?
            </Title>
            <Text size="lg" c="dimmed" maw={500} mx="auto">
              Upload a photo and get a confident result — no more guessing between Soft Autumn and Deep Autumn.
            </Text>
            <Flex gap="md" justify="center" wrap="wrap">
              <Button
                size="lg"
                component={Link}
                href="/ai-color-analysis"
                color="violet"
                rightSection={<IconArrowRight size={20} />}
              >
                Confirm My Season
              </Button>
              <Button
                size="lg"
                variant="outline"
                component={Link}
                href="/color-analysis-quiz"
                color="violet"
              >
                Start With Free Quiz
              </Button>
            </Flex>
            <Group justify="center" gap="xl" mt="md">
              <Button
                variant="subtle"
                component={Link}
                href="/color-palette/autumn/soft"
                size="sm"
                color="gray"
              >
                View Soft Autumn Palette →
              </Button>
              <Button
                variant="subtle"
                component={Link}
                href="/color-palette/autumn/deep"
                size="sm"
                color="gray"
              >
                View Deep Autumn Palette →
              </Button>
            </Group>
          </Stack>
        </Card>
      </Stack>
    </Container>
  );
}
